/**
 * Quantity-qualifier (NCI potency-unit) matching for ScriptSure prescribing.
 *
 * ScriptSure's `$order-medication` takes a `quantityQualifier` — the NCI Thesaurus potency-unit code
 * for the dispense unit (tablet, mL, patch, …). {@link STATIC_QUANTITY_QUALIFIERS} below is the
 * catalog; this module infers the right code from free text (a sig line and/or a formulation label)
 * so the prescribe form can pre-fill it.
 *
 * Ported from the Medplum provider example (`medplum-provider/src/components/meds/quantity-qualifiers.ts`),
 * adapted to Liza's `{ code, label }` catalog shape (the provider app uses `{ potencyUnit, name }`).
 */

/** A `{ code, label }` dispense-unit qualifier (NCI potency-unit code). */
export interface QuantityQualifier {
  readonly code: string;
  readonly label: string;
}

/** Default qualifier: NCI "Tablet dosing unit". The fallback when nothing else can be inferred. */
export const DEFAULT_QUANTITY_QUALIFIER = 'C48542';

/**
 * NCI Thesaurus potency-unit codes used by ScriptSure as quantity qualifiers. **The only source** —
 * we no longer call the vendor's `$drug-quantity-qualifiers` operation for this list.
 *
 * That call was dropped because it resolves a ScriptSure practice like every other vendor call, but
 * carries no patient and so has no practice to send: it only ever worked via the prescriber's
 * `PractitionerRole` affiliation, and failed outright for callers without one (CI, and any flow
 * where the affiliation is absent). Paying a per-open vendor round-trip — and a practice
 * dependency — for a list this static wasn't worth it.
 *
 * The trade-off: this table is a verbatim snapshot of ScriptSure's catalog and nothing now detects
 * drift automatically. If the vendor adds or renumbers a unit, re-fetch the live list and diff it
 * here, checking whether a new label needs a {@link QUALIFIER_SYNONYMS_BY_LABEL} entry. Getting
 * this wrong is not cosmetic — an earlier version used guessed
 * sequential `C484xx` codes that were mostly wrong — e.g. it mapped Gram→C48478, but the catalog
 * says C48478 is **Box** and Gram is **C48155**; Each, Drop, Capsule, Patch, Milligram,
 * Suppository, Syringe, Spray, and Vial were likewise bound to wrong codes, so inference stamped
 * codes ScriptSure didn't recognize and pending orders fell back to its Tablet default (#6934).
 */
export const STATIC_QUANTITY_QUALIFIERS: readonly QuantityQualifier[] = [
  { code: 'C48473', label: 'Ampule' },
  { code: 'C62412', label: 'Applicator' },
  { code: 'C78783', label: 'Applicatorful' },
  { code: 'C48474', label: 'Bag' },
  { code: 'C48475', label: 'Bar' },
  { code: 'C53495', label: 'Bead' },
  { code: 'C54564', label: 'Blister' },
  { code: 'C53498', label: 'Block' },
  { code: 'C48476', label: 'Bolus' },
  { code: 'C48477', label: 'Bottle' },
  { code: 'C48478', label: 'Box' },
  { code: 'C48479', label: 'Can' },
  { code: 'C62413', label: 'Canister' },
  { code: 'C64696', label: 'Caplet' },
  { code: 'C48480', label: 'Capsule' },
  { code: 'C54702', label: 'Carton' },
  { code: 'C48481', label: 'Cartridge' },
  { code: 'C62414', label: 'Case' },
  { code: 'C69093', label: 'Cassette' },
  { code: 'C48484', label: 'Container' },
  { code: 'C48489', label: 'Cylinder' },
  { code: 'C16830', label: 'Device' },
  { code: 'C48490', label: 'Disk' },
  { code: 'C62417', label: 'Dose Pack' },
  { code: 'C48491', label: 'Drop' },
  { code: 'C96265', label: 'Dual Pack' },
  { code: 'C64933', label: 'Each' },
  { code: 'C53499', label: 'Film' },
  { code: 'C48494', label: 'Fluid Ounce' },
  { code: 'C101680', label: 'French' },
  { code: 'C48580', label: 'Gallon' },
  { code: 'C48155', label: 'Gram' },
  { code: 'C69124', label: 'Gum' },
  { code: 'C48499', label: 'Implant' },
  { code: 'C48501', label: 'Inhalation' },
  { code: 'C62275', label: 'Inhaler' },
  { code: 'C62418', label: 'Inhaler Refill' },
  { code: 'C62276', label: 'Insert' },
  { code: 'C67283', label: 'Intravenous Bag' },
  { code: 'C28252', label: 'Kilogram' },
  { code: 'C48504', label: 'Kit' },
  { code: 'C120263', label: 'Lancet' },
  { code: 'C48505', label: 'Liter' },
  { code: 'C48506', label: 'Lozenge' },
  { code: 'C48152', label: 'Microgram' },
  { code: 'C48512', label: 'Milliequivalent' },
  { code: 'C28253', label: 'Milligram' },
  { code: 'C28254', label: 'Milliliter' },
  { code: 'C28251', label: 'Millimeter' },
  { code: 'C71204', label: 'Nebule' },
  { code: 'C100052', label: 'Needle Free Injection' },
  { code: 'C69086', label: 'Ocular System' },
  { code: 'C48519', label: 'Ounce' },
  { code: 'C48520', label: 'Package' },
  { code: 'C48521', label: 'Packet' },
  { code: 'C65032', label: 'Pad' },
  { code: 'C82484', label: 'Paper' },
  { code: 'C48524', label: 'Patch' },
  { code: 'C120216', label: 'Pen Needle' },
  { code: 'C48529', label: 'Pint' },
  { code: 'C48530', label: 'Pouch' },
  { code: 'C48531', label: 'Pound' },
  { code: 'C97717', label: 'Pre-filled Pen Syringe' },
  { code: 'C65060', label: 'Puff' },
  { code: 'C111984', label: 'Pump' },
  { code: 'C48534', label: 'Quart' },
  { code: 'C62609', label: 'Ring' },
  { code: 'C71324', label: 'Sachet' },
  { code: 'C48536', label: 'Scoopful' },
  { code: 'C53502', label: 'Sponge' },
  { code: 'C48537', label: 'Spray' },
  { code: 'C53503', label: 'Stick' },
  { code: 'C48538', label: 'Strip' },
  { code: 'C48539', label: 'Suppository' },
  { code: 'C53504', label: 'Swab' },
  { code: 'C48540', label: 'Syringe' },
  { code: 'C48541', label: 'Tablespoon' },
  { code: 'C48542', label: 'Tablet' },
  { code: 'C62421', label: 'Tabminder' },
  { code: 'C48543', label: 'Tampon' },
  { code: 'C48544', label: 'Teaspoon' },
  { code: 'C54704', label: 'Tray' },
  { code: 'C48548', label: 'Troche' },
  { code: 'C48549', label: 'Tube' },
  { code: 'C44278', label: 'Unit' },
  { code: 'C38046', label: 'Unspecified' },
  { code: 'C48551', label: 'Vial' },
  { code: 'C48552', label: 'Wafer' },
];

/**
 * Priority tiers for qualifier rules. Higher-priority rules win during matching regardless of how
 * long the catalog label happens to be:
 *  - `DOSE_FORM` — solid/discrete dispense units (tablet, capsule, suppository, …). Always beat
 *    strength units in mixed text like "Metformin ER 500 mg tablet" (mg is a strength, not the unit).
 *  - `DOSE_FORM_SPECIFIC` — +10 bump so a more-specific entry beats a sibling ("Applicatorful" >
 *    "Applicator").
 *  - `LIQUID_VOLUME` — `mL`, which *can* be the dispense unit (liquids) but loses to dose forms.
 *  - `CATALOG_NAME` — default for live-catalog entries we have no synonym for; we generate
 *    `\b<label>s?\b` and trust future codes look like real words.
 *  - strength units (mg/mcg/IU) emit no rule at all (mapped to `null`) — never a dispense unit —
 *    but stay selectable via {@link mergeQuantityQualifierCatalog}. Gram is the exception: it IS the
 *    dispense unit for topicals, so it gets a rule matched on topical dose-form words
 *    (cream/ointment/gel/paste) — never on the word "gram" itself, which stays a strength.
 */
const PRIORITY_DOSE_FORM = 100;
const PRIORITY_DOSE_FORM_SPECIFIC = 110;
const PRIORITY_LIQUID_VOLUME = 30;
const PRIORITY_CATALOG_NAME = 50;

interface SynonymGroup {
  readonly priority: number;
  readonly patterns: readonly RegExp[];
}

const TABLET_GROUP: SynonymGroup = { priority: PRIORITY_DOSE_FORM, patterns: [/\b(?:tablets?|tabs?)\b/i] };
const CAPSULE_GROUP: SynonymGroup = { priority: PRIORITY_DOSE_FORM, patterns: [/\b(?:capsules?|caps?)\b/i] };

/**
 * Synonym patterns + priority for qualifier rows, keyed by the lowercased catalog `label` (and
 * practical aliases — ScriptSure ships "Tablet dosing unit" but sigs say "tablet"/"tab"). A `null`
 * value excludes the row from the matcher (strength units like Milligram); it stays selectable in the
 * dropdown via {@link mergeQuantityQualifierCatalog}.
 */
const QUALIFIER_SYNONYMS_BY_LABEL: Readonly<Record<string, SynonymGroup | null>> = {
  tablet: TABLET_GROUP,
  'tablet dosing unit': TABLET_GROUP,
  capsule: CAPSULE_GROUP,
  'capsule dosing unit': CAPSULE_GROUP,
  suppository: { priority: PRIORITY_DOSE_FORM, patterns: [/\bsuppositor(?:y|ies)\b/i] },
  patch: { priority: PRIORITY_DOSE_FORM, patterns: [/\bpatch(?:es)?\b/i] },
  spray: { priority: PRIORITY_DOSE_FORM, patterns: [/\bsprays?\b/i] },
  drop: { priority: PRIORITY_DOSE_FORM, patterns: [/\bdrops?\b/i] },
  // "oral/dosing/measuring syringe" is the measuring device in a liquid sig ("take 5 mL using the
  // oral syringe"), not the dispense unit — the lookbehind keeps mL the winner there while a real
  // syringe product ("prefilled syringe") still matches.
  syringe: { priority: PRIORITY_DOSE_FORM, patterns: [/(?<!(?:oral|dosing|measuring) )\bsyringes?\b/i] },
  vial: { priority: PRIORITY_DOSE_FORM, patterns: [/\bvials?\b/i] },
  ampule: { priority: PRIORITY_DOSE_FORM, patterns: [/\b(?:ampoules?|ampules?)\b/i] },
  ampoule: { priority: PRIORITY_DOSE_FORM, patterns: [/\b(?:ampoules?|ampules?)\b/i] },
  bag: { priority: PRIORITY_DOSE_FORM, patterns: [/\bbags?\b/i] },
  bar: { priority: PRIORITY_DOSE_FORM, patterns: [/\bbars?\b/i] },
  cartridge: { priority: PRIORITY_DOSE_FORM, patterns: [/\bcartridges?\b/i] },
  applicator: { priority: PRIORITY_DOSE_FORM, patterns: [/\bapplicators?\b/i] },
  applicatorful: { priority: PRIORITY_DOSE_FORM_SPECIFIC, patterns: [/\bapplicatorful?s?\b/i] },
  // Specific before generic, like Applicatorful > Applicator: "pre-filled pen syringe" must beat
  // the plain Syringe rule, "intravenous bag" the plain Bag rule.
  'pre-filled pen syringe': { priority: PRIORITY_DOSE_FORM_SPECIFIC, patterns: [/\bpre-?filled pen syringes?\b/i] },
  'intravenous bag': { priority: PRIORITY_DOSE_FORM_SPECIFIC, patterns: [/\bintravenous bags?\b/i] },
  milliliter: { priority: PRIORITY_LIQUID_VOLUME, patterns: [/\b(?:milliliters?|millilitres?|ml|mls)\b/i] },
  millilitre: { priority: PRIORITY_LIQUID_VOLUME, patterns: [/\b(?:milliliters?|millilitres?|ml|mls)\b/i] },
  milligram: null,
  microgram: null,
  // Gram is the dispense unit for topicals, matched via the topical dose-form words — never via
  // "gram(s)" itself, which in sig/formulation text is a strength, not a dispense unit (#6934).
  gram: { priority: PRIORITY_DOSE_FORM, patterns: [/\b(?:creams?|ointments?|gels?|pastes?)\b/i] },
  'international unit': null,
  // `Unit` is a catalog row (selectable in the dropdown) but excluded from inference: "10 units" in
  // an insulin sig is a dose amount, not the dispense unit.
  unit: null,
  // `Day` and `Each` are catalog rows (selectable in the dropdown) but excluded from inference: a
  // generated `\bdays?\b` rule would otherwise fire at CATALOG_NAME priority (50) — above mL's
  // LIQUID_VOLUME (30) — and mis-tag any sig mentioning "days" (e.g. "5 mL ... for 30 days") as Day.
  day: null,
  each: null,
  // Everyday words that show up in sig/instruction prose without being the dispense unit — an
  // auto-generated `\b<label>s?\b` rule would fire at CATALOG_NAME priority (50), above mL (30):
  // "shake the bottle well", "you can take", "in case of", "read the package insert", "apply along
  // the gum line", "give 5 mL via G-tube", "apply to blisters". Excluded from inference; all stay
  // selectable in the dropdown. (Topicals dispensed per tube still resolve Gram via the topical
  // dose-form rule; blister-packed meds resolve Tablet/Capsule — both outrank a generated rule.)
  blister: null,
  block: null,
  bolus: null,
  bottle: null,
  box: null,
  can: null,
  carton: null,
  case: null,
  container: null,
  device: null,
  gum: null,
  insert: null,
  package: null,
  paper: null,
  stick: null,
  tray: null,
  tube: null,
  unspecified: null,
  // Dose-measure and size units (like mg/mcg above): what the patient takes per dose or the
  // product's sizing, never what the pharmacy dispenses.
  'fluid ounce': null,
  french: null,
  gallon: null,
  kilogram: null,
  liter: null,
  milliequivalent: null,
  millimeter: null,
  ounce: null,
  pint: null,
  pound: null,
  quart: null,
  tablespoon: null,
  teaspoon: null,
};

/** Escape a string for use as a literal inside a RegExp. */
function escapeRegexLiteral(text: string): string {
  // RegExp.escape (ES2025) lands in Node 23+; CI runs Node 22.
  if (typeof RegExp.escape === 'function') {
    return RegExp.escape(text);
  }
  return text.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

/**
 * Returns the best NCI potency-unit code for a free-text fragment (sig line, formulation label, …),
 * or `undefined` when nothing matches. Built from a qualifier catalog by {@link buildQualifierMatcher}.
 */
export type QualifierMatcher = (text: string | undefined) => string | undefined;

interface CompiledRule {
  code: string;
  pattern: RegExp;
  priority: number;
  labelLength: number;
}

/**
 * Compile a {@link QualifierMatcher} from a qualifier catalog. Each row is classified by its
 * lowercased `label`: a known dose form / liquid volume gets explicit synonym patterns; a strength
 * unit is skipped (never a dispense unit); anything else gets `\b<label>s?\b` at `CATALOG_NAME`
 * priority so a brand-new code DAW ships is still recognized without a code change. Rules sort by
 * priority desc, then label length desc as a tiebreaker.
 */
export function buildQualifierMatcher(catalog: readonly QuantityQualifier[]): QualifierMatcher {
  const compiled: CompiledRule[] = [];
  for (const row of catalog) {
    const code = row.code?.trim();
    const label = row.label?.trim();
    if (!code || !label) {
      continue;
    }
    const synonym = QUALIFIER_SYNONYMS_BY_LABEL[label.toLowerCase()];
    if (synonym === null) {
      continue;
    }
    if (synonym) {
      for (const pattern of synonym.patterns) {
        compiled.push({ code, pattern, priority: synonym.priority, labelLength: label.length });
      }
      continue;
    }
    const escaped = escapeRegexLiteral(label);
    compiled.push({
      code,
      pattern: new RegExp(String.raw`\b${escaped}s?\b`, 'i'),
      priority: PRIORITY_CATALOG_NAME,
      labelLength: label.length,
    });
  }

  compiled.sort((a, b) => b.priority - a.priority || b.labelLength - a.labelLength);

  return (text) => {
    if (!text) {
      return undefined;
    }
    for (const rule of compiled) {
      if (rule.pattern.test(text)) {
        return rule.code;
      }
    }
    return undefined;
  };
}

/** Default {@link QualifierMatcher} from the static catalog — used before the live catalog loads. */
export const STATIC_QUALIFIER_MATCHER: QualifierMatcher = buildQualifierMatcher(STATIC_QUANTITY_QUALIFIERS);

/**
 * Infer a quantity-qualifier (NCI potency-unit) code from free text — a sig line ("Insert 1
 * suppository rectally daily") and/or a formulation label ("Anusol-HC 25 mg rectal suppository").
 *
 * `matcher` must come from {@link buildQualifierMatcher} — pass {@link STATIC_QUALIFIER_MATCHER} when
 * the live `$drug-quantity-qualifiers` catalog hasn't loaded, or build one from the live catalog so
 * newly added DAW codes are recognized. Returns a non-empty code or `undefined` (never `''`).
 */
export function inferQuantityQualifierCode(
  matcher: QualifierMatcher,
  ...textParts: (string | undefined)[]
): string | undefined {
  const haystack = textParts.filter(Boolean).join(' ');
  if (!haystack.trim()) {
    return undefined;
  }
  return matcher(haystack);
}

/**
 * Resolve the dispense unit (NCI potency code) for a sig. The matcher only fires on dose-form / `mL`
 * keywords, never a strength unit, so when it returns something we trust it over ScriptSure's per-sig
 * `quantityQualifier` (which often carries a strength-unit code for solid dose forms — e.g. metformin
 * tablets coming back with the milligram code because the formulation strength is "500 mg").
 *
 * Priority: (1) keyword inference from sig + formulation label; (2) the raw value ScriptSure sent
 * (when non-empty); (3) the static `C48542` Tablet fallback. Never returns empty.
 */
export function resolveQuantityQualifier(
  raw: string | undefined,
  sigLine: string,
  formatText: string | undefined,
  matcher: QualifierMatcher,
): string {
  return inferQuantityQualifierCode(matcher, sigLine, formatText) || raw?.trim() || DEFAULT_QUANTITY_QUALIFIER;
}

/** Merge live catalog rows over the static fallback (live wins on duplicate codes); sorted by label. */
export function mergeQuantityQualifierCatalog(live: readonly QuantityQualifier[]): QuantityQualifier[] {
  const map = new Map<string, string>();
  for (const row of STATIC_QUANTITY_QUALIFIERS) {
    map.set(row.code, row.label);
  }
  for (const row of live) {
    if (row.code && row.label) {
      map.set(row.code, row.label);
    }
  }
  return [...map.entries()].map(([code, label]) => ({ code, label })).sort((a, b) => a.label.localeCompare(b.label));
}
